import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * Schema migrations — platform administrator.
 *
 * Reads GET /v1/platform/schema-migrations, which is behind PlatformAdminGuard.
 * This is not tenant data: a migration alters the database every tenant shares,
 * so no tenant administrator sees it.
 *
 * Read-only by design. Applying a pending migration is a later phase; offering
 * an Apply button before the cross-module order has been proven would invite
 * the half-applied schema this ledger exists to prevent.
 */

interface StateCount {
  readonly state: string;
  readonly count: number;
}

interface Summary {
  readonly total: number;
  readonly ordered: number;
  readonly modules: number;
  readonly lastAppliedAt: string | null;
  // Not a fixed set of fields. The API returns whatever states the database
  // permits or holds, so a state added by a platform administrator shows up
  // here without this screen being edited.
  readonly states: readonly StateCount[];
}

interface ModuleStateRow {
  readonly moduleCode: string;
  readonly state: string;
  readonly count: number;
}

interface FileRow {
  readonly relativePath: string;
  readonly moduleCode: string;
  readonly fileName: string;
  readonly state: string;
  readonly appliedAt: string | null;
  readonly durationMs: number | null;
  readonly applySequence: number | null;
  readonly errorDetail: string | null;
  readonly checksumShort: string;
}

/** One disagreement between the migration files and what this database ran. */
interface DriftRow {
  readonly kind: 'UNRECORDED' | 'MISSING' | 'CHANGED' | 'ASSERTED' | 'FAILED';
  readonly relativePath: string;
  readonly moduleCode: string;
  readonly detail: string | null;
}

interface Conformance {
  readonly filesAvailable: boolean;
  readonly filesRoot: string | null;
  readonly filesOnDisk: number;
  readonly ledgerRows: number;
  readonly drift: readonly DriftRow[];
  readonly counts: Readonly<Record<DriftRow['kind'], number>>;
  readonly inStep: boolean;
}

export function SchemaMigrationsPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [modules, setModules] = useState<readonly ModuleStateRow[] | null>(null);
  const [files, setFiles] = useState<readonly FileRow[] | null>(null);
  const [conformance, setConformance] = useState<Conformance | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, m, f, c] = await Promise.all([
          apiRequest<Summary>('/v1/platform/schema-migrations/summary', { token }),
          apiRequest<{ items: readonly ModuleStateRow[] }>('/v1/platform/schema-migrations/by-module', { token }),
          apiRequest<{ items: readonly FileRow[] }>('/v1/platform/schema-migrations', { token }),
          apiRequest<Conformance>('/v1/platform/schema-migrations/conformance', { token }),
        ]);
        if (!alive) return;
        setSummary(s);
        setModules(m.items ?? []);
        setFiles(f.items ?? []);
        setConformance(c);
        setError(null);
      } catch (e) {
        // A ledger that fails to load must say so. An empty table here would
        // read as "no migrations", which is the very blindness this replaces.
        if (alive) setError((e as Error).message);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (error) return <main aria-label="schema-migrations"><p role="alert" aria-label="sm-error">{error}</p></main>;
  if (!summary || !modules || !files || !conformance) {
    return <main aria-label="schema-migrations"><p aria-label="sm-loading">{t('common.loading')}</p></main>;
  }

  // A state's label is looked up, and falls back to the code itself. A state
  // this build has never heard of is still shown, spelled as the database
  // spells it, rather than being silently dropped.
  const stateLabel = (state: string): string => {
    const key = `platform.sm.state.${state}`;
    const label = t(key);
    return label === key ? state : label;
  };

  const kpis: ReadonlyArray<readonly [string, string, number | string]> = [
    ['total', t('platform.sm.kpiTotal'), summary.total],
    ...summary.states.map(
      (s) => [`state-${s.state}`, stateLabel(s.state), s.count] as const,
    ),
    ['ordered', t('platform.sm.kpiOrdered'), summary.ordered],
    ['modules', t('platform.sm.kpiModules'), summary.modules],
  ];

  // Pivot the per-module rows into one row per module with a column per state,
  // so the table widens by itself when a state is added.
  const stateCodes = summary.states.map((s) => s.state);
  const moduleRows = [...new Set(modules.map((r) => r.moduleCode))]
    .map((moduleCode) => {
      const rows = modules.filter((r) => r.moduleCode === moduleCode);
      const counts: Record<string, number> = {};
      for (const c of stateCodes) counts[c] = 0;
      for (const r of rows) counts[r.state] = r.count;
      return {
        moduleCode,
        files: rows.reduce((n, r) => n + r.count, 0),
        counts,
      };
    })
    .sort((a, b) => b.files - a.files || a.moduleCode.localeCompare(b.moduleCode));

  return (
    <main aria-label="schema-migrations">
      <h1>{t('platform.sm.title')}</h1>
      <p aria-label="sm-intro">{t('platform.sm.intro')}</p>

      <section aria-label="sm-kpis">
        {kpis.map(([key, label, value]) => (
          <div key={key} aria-label={`sm-kpi-${key}`}>
            <span>{label}</span> <strong aria-label={`sm-kpi-value-${key}`}>{value}</strong>
          </div>
        ))}
      </section>

      {/* The verdict, stated before the counts are read. A database can report
          every row it holds and still not match the code it was built from;
          that gap is what this screen exists to show. */}
      <section aria-label="sm-conformance">
        <h2>{t('platform.sm.conformance')}</h2>
        {!conformance.filesAvailable ? (
          <p role="alert" aria-label="sm-files-unavailable">
            {t('platform.sm.filesUnavailable')}
          </p>
        ) : conformance.inStep ? (
          <p role="status" aria-label="sm-in-step">
            {t('platform.sm.inStep', {
              files: String(conformance.filesOnDisk),
              rows: String(conformance.ledgerRows),
            })}
          </p>
        ) : (
          <p role="alert" aria-label="sm-out-of-step">
            {t('platform.sm.outOfStep', {
              unrecorded: String(conformance.counts.UNRECORDED),
              missing: String(conformance.counts.MISSING),
              changed: String(conformance.counts.CHANGED),
              failed: String(conformance.counts.FAILED),
            })}
          </p>
        )}
        {conformance.counts.ASSERTED > 0 ? (
          <p role="status" aria-label="sm-asserted">
            {t('platform.sm.asserted', {
              count: String(conformance.counts.ASSERTED),
            })}
          </p>
        ) : null}
        {conformance.drift.length > 0 ? (
          <DataTable<DriftRow>
            testId="sm-drift-table"
            ariaLabel="sm-drift-table"
            rowKey={(r) => `${r.kind}:${r.relativePath}`}
            rowAriaLabel={(r) => `sm-drift-${r.relativePath}`}
            columns={[
              { key: 'kind', header: t('platform.sm.colKind'), render: (r) => t(`platform.sm.drift.${r.kind}`) === `platform.sm.drift.${r.kind}` ? r.kind : t(`platform.sm.drift.${r.kind}`) },
              { key: 'module', header: t('platform.sm.colModule'), render: (r) => r.moduleCode },
              { key: 'file', header: t('platform.sm.colFile'), render: (r) => r.relativePath },
              { key: 'detail', header: t('platform.sm.colNote'), render: (r) => r.detail ?? '—' },
            ]}
            rows={conformance.drift as DriftRow[]}
          />
        ) : null}
      </section>

      {summary.states.every((s) => s.count === 0 || s.state === 'DISCOVERED') ? (
        <p role="status" aria-label="sm-not-baselined">{t('platform.sm.notBaselined')}</p>
      ) : null}

      <section aria-label="sm-by-module">
        <h2>{t('platform.sm.byModule')}</h2>
        <DataTable<typeof moduleRows[number]>
          testId="sm-module-table"
          ariaLabel="sm-module-table"
          rowKey={(r) => r.moduleCode}
          rowAriaLabel={(r) => `sm-module-${r.moduleCode}`}
          columns={[
            { key: 'module', header: t('platform.sm.colModule'), render: (r) => r.moduleCode },
            { key: 'files', header: t('platform.sm.colFiles'), render: (r) => r.files },
            // One column per state the database reported, in the order the
            // M01_LEDGER_STATE_ORDER parameter puts them.
            ...stateCodes.map((code) => ({
              key: `state-${code}`,
              header: stateLabel(code),
              render: (r: typeof moduleRows[number]) => r.counts[code] ?? 0,
            })),
          ]}
          rows={moduleRows}
        />
      </section>

      <section aria-label="sm-files">
        <h2>{t('platform.sm.files')}</h2>
        <DataTable<FileRow>
          testId="sm-file-table"
          ariaLabel="sm-file-table"
          rowKey={(r) => r.relativePath}
          rowAriaLabel={(r) => `sm-file-${r.relativePath}`}
          columns={[
            { key: 'file', header: t('platform.sm.colFile'), render: (r) => r.fileName },
            { key: 'module', header: t('platform.sm.colModule'), render: (r) => r.moduleCode },
            { key: 'state', header: t('vops.common.status'), render: (r) => r.state },
            {
              key: 'seq',
              header: t('platform.sm.colSequence'),
              // Null is not "unknown by accident": it means the replay could
              // not place this file against an empty database.
              render: (r) => (r.applySequence == null ? '—' : r.applySequence),
            },
            {
              key: 'applied',
              header: t('platform.sm.colApplied'),
              render: (r) => (r.appliedAt ? at(r.appliedAt) : '—'),
            },
            { key: 'checksum', header: t('platform.sm.colChecksum'), render: (r) => r.checksumShort },
            { key: 'note', header: t('platform.sm.colNote'), render: (r) => r.errorDetail ?? '—' },
          ]}
          rows={files}
        />
      </section>
    </main>
  );
}
